<script setup>
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';

const props = defineProps({
  step: {
    type: String,
    required: true,
  },
  items: {
    type: Array,
    default: () => [],
  },
  checklist: {
    type: Object,
    default: () => ({}),
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  // Item key -> { url, label }. An item that has one gets a link to where the thing is
  // verified at Meta, rendered outside the label so the confirmation stays plain
  // translatable text rather than markup inside a translation.
  links: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['confirm']);

const label = item =>
  `CLIENT_SETTINGS.CURRENCY_MIGRATION.CHECKLIST.${item.toUpperCase()}`;

// Confirmations are persisted one at a time: the admin ticks items over minutes while
// verifying things in Meta Business Suite, and a reload must not lose that work.
const toggle = (item, value) => {
  if (props.disabled) return;
  emit('confirm', { [item]: value });
};
</script>

<template>
  <ul class="flex flex-col gap-2 m-0 list-none">
    <li v-for="item in items" :key="item" class="flex items-start gap-2.5">
      <!-- @click.stop keeps the row handler below from undoing the checkbox's own toggle. -->
      <span class="mt-0.5 shrink-0" @click.stop>
        <Checkbox
          :model-value="Boolean(checklist[item])"
          :disabled="disabled"
          :data-testid="`checklist-${step}-${item}`"
          @update:model-value="value => toggle(item, value)"
        />
      </span>
      <span class="flex flex-wrap items-baseline gap-x-2 text-sm leading-5">
        <span
          class="text-n-slate-12"
          :class="disabled ? 'cursor-default' : 'cursor-pointer'"
          @click="toggle(item, !checklist[item])"
        >
          {{ $t(label(item)) }}
        </span>
        <!-- Outside the toggle above: clicking the link must open Meta, not tick the item. -->
        <a
          v-if="links[item]"
          :href="links[item].url"
          target="_blank"
          rel="noopener noreferrer"
          class="text-n-blue-text hover:underline"
          :data-testid="`checklist-link-${item}`"
        >
          {{ links[item].label }}
        </a>
      </span>
    </li>
  </ul>
</template>
